import React, { useMemo, useState } from 'react';
import { Lock, Plus, User, Pencil } from 'lucide-react';
import Modal from '../../../shared/components/ui/Modal';
import { ApplicantModal } from '../forms/ApplicantModal';
import QuickApplicantModal from './QuickApplicantModal';
import { uniqueApplicantsBySlot } from './postSalesKyc';

const MAX_APPLICANTS = 5;

const DealApplicantsSection = ({
  dealId,
  applicants: rawApplicants = [],
  onUpdated,
  /**
   * The deal's pricing has not been approved by Management yet.
   *
   * Same gate the Stage History uses: completing the Management Approval
   * activity is what sets the deal to Approved and creates the Application Form
   * step. Post Sales was letting people fill that form before the step existed,
   * so the two screens disagreed about whether the deal was ready.
   */
  awaitingApproval = false
}) => {
  const [showFullModal, setShowFullModal] = useState(false);
  const [quickApplicantNumber, setQuickApplicantNumber] = useState(null);

  const applicants = useMemo(
    () => uniqueApplicantsBySlot(rawApplicants),
    [rawApplicants]
  );

  const nextNumber =
    applicants.length > 0
      ? Math.min(
          MAX_APPLICANTS,
          Math.max(...applicants.map((a) => a.applicant_number || 1)) + 1
        )
      : 1;

  const canAddMore = applicants.length < MAX_APPLICANTS;

  const openAdd = () => {
    if (applicants.length === 0 || nextNumber <= 2) {
      setShowFullModal(true);
      return;
    }
    setQuickApplicantNumber(nextNumber);
  };

  return (
    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3 sm:px-5">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Applicants</h3>
          <p className="mt-1 text-sm text-gray-500">
            Buyer and co-applicants for agreements (up to {MAX_APPLICANTS}).
          </p>
        </div>
        {canAddMore &&
          (awaitingApproval ? (
            <span className="inline-flex items-center gap-2 rounded-lg bg-gray-100 px-3.5 py-2.5 text-sm font-semibold text-gray-500">
              <Lock className="h-4 w-4" aria-hidden />
              Locked
            </span>
          ) : (
            <button
              type="button"
              onClick={openAdd}
              className="inline-flex items-center gap-2 rounded-lg bg-[#2A565E] px-3.5 py-2.5 text-sm font-semibold text-white hover:bg-[#234952] focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1">
              <Plus className="h-4 w-4" aria-hidden />
              {applicants.length === 0 ? 'Add applicant' : 'Add co-applicant'}
            </button>
          ))}
      </div>

      <div className="p-4 sm:p-5">
        {awaitingApproval && !applicants.length ? (
          <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50/60 px-4 py-8 text-center">
            <Lock className="mx-auto h-8 w-8 text-amber-500" aria-hidden />
            <p className="mt-2 text-sm font-semibold text-amber-900">
              Waiting on management approval
            </p>
            <p className="mx-auto mt-1 max-w-md text-sm text-amber-800">
              The application form opens once the deal pricing is approved. You can see
              where the deal has got to under Stage history &amp; logs.
            </p>
          </div>
        ) : !applicants.length ? (
          <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/60 px-4 py-8 text-center">
            <User className="mx-auto h-8 w-8 text-gray-400" />
            <p className="mt-2 text-sm font-medium text-gray-800">No applicants yet</p>
            <p className="mt-1 text-xs text-gray-500">
              Add the primary buyer first — then co-applicants if needed.
            </p>
          </div>
        ) : (
          <ul className={`grid gap-3 ${applicants.length > 1 ? 'sm:grid-cols-2' : ''}`}>
            {applicants.map((app) => (
              <li
                key={app.id ?? app.applicant_number}
                className="rounded-xl border border-gray-200 bg-gray-50/40 p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wide text-teal-800">
                      {app.applicant_number === 1
                        ? 'Primary buyer'
                        : `Co-applicant ${app.applicant_number - 1}`}
                    </p>
                    <p className="mt-1 text-base font-semibold text-gray-900">
                      {app.name || '—'}
                    </p>
                    <p className="mt-1 text-sm text-gray-600">
                      {app.mobile || app.email || '—'}
                    </p>
                    {(app.pan || app.aadhar) && (
                      <p className="mt-2 text-xs text-gray-500">
                        {app.pan ? `PAN ${app.pan}` : ''}
                        {app.pan && app.aadhar ? ' · ' : ''}
                        {app.aadhar ? `Aadhaar ${app.aadhar}` : ''}
                      </p>
                    )}
                  </div>
                  {app.applicant_number <= 2 && (
                    <button
                      type="button"
                      title="Edit in full form"
                      onClick={() => setShowFullModal(true)}
                      className="rounded-lg border border-gray-200 bg-white p-2 text-gray-600 hover:bg-gray-50">
                      <Pencil className="h-4 w-4" aria-hidden />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {showFullModal && (
        <Modal name="Application form" width={70} onClose={() => setShowFullModal(false)}>
          <ApplicantModal
            dealId={dealId}
            onClose={() => {
              setShowFullModal(false);
              onUpdated?.();
            }}
          />
        </Modal>
      )}

      {quickApplicantNumber != null && (
        <Modal
          name={`Co-applicant ${quickApplicantNumber - 1}`}
          width={50}
          onClose={() => setQuickApplicantNumber(null)}>
          <QuickApplicantModal
            dealId={dealId}
            applicantNumber={quickApplicantNumber}
            onClose={() => setQuickApplicantNumber(null)}
            onSuccess={() => {
              setQuickApplicantNumber(null);
              onUpdated?.();
            }}
          />
        </Modal>
      )}
    </section>
  );
};

export default DealApplicantsSection;
